/*
Copyright 2025 TekNix Corporation

This software is proprietary and confidential to TekNix Corporation.
All rights reserved. No part of this software may be reproduced, distributed,
or transmitted in any form or by any means, including photocopying, recording,
or other electronic or mechanical methods, without the prior written permission
of TekNix Corporation, except in the case of brief quotations embodied in
critical reviews and certain other noncommercial uses permitted by copyright law.

For permission requests, write to TekNix Corporation at the address below:
TekNix Corporation
Legal Department

UNAUTHORIZED REPRODUCTION OR DISTRIBUTION IS STRICTLY PROHIBITED.
*/

import type { IStartClientOpts } from '../@types/client';
import type { Comment, IEvent, IEventRelation, IMinimalEvent } from '../@types/event';
import type { Extension } from '../@types/extension';
import type { XHubChatClient } from '../client';
import type { XHubChatEvent } from '../models/event/event';

import type { CentrifugeRealtime } from '../models/realtime/centrifuge-realtime';
import type { RawNotiMessage, RealtimeRoomUpdatePayload } from '../models/realtime/interface';
import type { IStoredClientOpts } from '../store/interfaces/store';
import type {
  ConnectionState,
  IRoomEvent,
  IStateEvent,
  ISyncStateData,
  QueuedMessage,
  ReconnectionStrategy,
  RoomData,
  SlidingSyncEventHandlerMap,
  SlidingSyncList,
  SlidingSyncResponse,
  SyncApiOptions,
} from './interface';
import { ClientEvent, EventType, MsgType } from '../@types/event';
import { ExtensionState } from '../@types/extension';
import { KnownMembership } from '../@types/membership';
import { NotificationCountType, RoomCategory, RoomEvent } from '../@types/room';
import { RoomStateEvent } from '../@types/room-state';
import { TypedEventEmitter } from '../emit/typed-event-emitter';
import { XHubChatError } from '../http';
import { EventTimeline } from '../models/event/event-timeline';
import { RealtimeEvent, RealtimeTransactionType } from '../models/realtime/interface';
import { Room } from '../models/room/room';
import { ExtensionPresence, ExtensionReceipts } from './extensions';
import { ConnectionState as ConnState, SlidingSyncEvent, SlidingSyncState, SyncState } from './interface';

/**
 * Internal Class. SlidingList represents a single list in sliding sync. The list can have filters,
 * multiple sliding windows, and maintains the index-\>room_id mapping.
 */
class SlidingList {
  private list!: SlidingSyncList;
  private isModified?: boolean;

  // returned data
  public joinedCount = 0;

  /**
   * Construct a new sliding list.
   * @param list - The range, sort and filter values to use for this list.
   */
  public constructor(list: SlidingSyncList) {
    this.replaceList(list);
  }

  /**
   * Mark this list as modified or not. Modified lists will return sticky params with calls to getList.
   * This is useful for the first time the list is sent, or if the list has changed in some way.
   * @param modified - True to mark this list as modified so all sticky parameters will be re-sent.
   */
  public setModified(modified: boolean): void {
    this.isModified = modified;
  }

  /**
   * Update the list range for this list. Does not affect modified status as list ranges are non-sticky.
   * @param newRanges - The new ranges for the list
   */
  public updateListRange(newRanges: number[][]): void {
    this.list.ranges = JSON.parse(JSON.stringify(newRanges));
  }

  /**
   * Replace list parameters. All fields will be replaced with the new list parameters.
   * @param list - The new list parameters
   */
  public replaceList(list: SlidingSyncList): void {
    list.ranges = list.ranges ?? [];
    this.list = JSON.parse(JSON.stringify(list));
    this.isModified = true;

    // reset values as the join count may be very different (if filters changed) including the rooms
    // (e.g. sort orders or sliding window ranges changed)

    // the total number of joined rooms according to the server, always >= len(roomIndexToRoomId)
    this.joinedCount = 0;
  }

  /**
   * Return a copy of the list suitable for a request body.
   * @param forceIncludeAllParams - True to forcibly include all params even if the list
   * hasn't been modified. Callers may want to do this if they are modifying the list prior to calling
   * updateList.
   */
  public getList(forceIncludeAllParams: boolean): SlidingSyncList {
    let list = {
      ranges: JSON.parse(JSON.stringify(this.list.ranges)),
    };
    if (this.isModified || forceIncludeAllParams) {
      list = JSON.parse(JSON.stringify(this.list));
    }
    return list;
  }
}

// Number of consecutive failed syncs that will lead to a syncState of ERROR as opposed
// to RECONNECTING. This is needed to inform the client of server issues when the
// keepAlive is successful but the server /sync fails.
const FAILED_SYNC_ERROR_THRESHOLD = 3;

type TaggedEvent = (IRoomEvent | IStateEvent | IMinimalEvent) & { room_id?: string };

/**
 *  Helper functions which set up JS SDK structs are below and are identical to the sync v2 counterparts, just outside the class.
 * @param client - XHubChatClient
 * @param roomId - string | undefined
 * @param events - object[] - Array of events from the server
 * @returns XHubChatEvent[]
 */
function mapEvents(client: XHubChatClient, roomId: string | undefined, events: object[]): XHubChatEvent[] {
  const mapper = client.getEventMapper();
  return (events as TaggedEvent[]).map((e) => {
    e.room_id = roomId;
    return mapper(e);
  });
}

// if async we don't care about the type as we only await resolution
export async function promiseMapSeries<T>(promises: Array<T | Promise<T>>, fn: (t: T) => Promise<unknown> | undefined): Promise<void> {
  for (const o of promises) {
    await fn(await o);
  }
}

// /!\ This function is not intended for public use! It's only exported from
// here in order to share some common logic with sliding-sync-sdk.ts.
export function _createAndReEmitRoom(client: XHubChatClient, roomId: string, roomData: RoomData, opts: Partial<IStoredClientOpts>): Room {
  const room = new Room(roomId, client, client.getUserId()!, roomData, {
    lazyLoadMembers: opts.lazyLoadMembers,
    pendingEventOrdering: opts.pendingEventOrdering,
  });

  client.reEmitter.reEmit(room, [
    RoomEvent.Name,
    RoomEvent.Receipt,
    RoomEvent.Tags,
    RoomEvent.LocalEchoUpdated,
    RoomEvent.AccountData,
    RoomEvent.MyMembership,
    RoomEvent.Timeline,
    RoomEvent.TimelineReset,
    RoomStateEvent.Events,
    RoomStateEvent.Members,
    RoomStateEvent.NewMember,
    RoomStateEvent.Update,
  ]);
  return room;
}

export class SlidingSync extends TypedEventEmitter<SlidingSyncEvent, SlidingSyncEventHandlerMap> {
  private lists: Map<string, SlidingList>;

  // === WebSocket Connection Management ===
  private connectionState: ConnectionState = ConnState.Disconnected;
  private reconnectAttempts = 0;
  private readonly maxReconnectAttempts = 10;
  private reconnectDelay = 1000; // Start with 1s
  private readonly maxReconnectDelay = 30000; // Max 30s
  private readonly reconnectBackoffMultiplier = 2;
  private isManualStop = false;
  private reconnectTimer?: ReturnType<typeof setTimeout>;

  // === Message Queue for Offline Support ===
  private messageQueue: QueuedMessage[] = [];
  private readonly maxQueueSize = 100;

  // === Health Check & Heartbeat ===
  private healthCheckInterval?: ReturnType<typeof setInterval>;
  private readonly healthCheckIntervalMs = 15 * 60 * 1000; // 15 minutes
  private lastHeartbeat?: number;
  private readonly heartbeatTimeout = 60 * 1000; // 60s

  // === Network Monitoring ===
  private isOnline = true;

  // === Legacy fields - kept for backward compatibility ===
  private lastPos: string | null = null;
  private syncState: SyncState | null = null;
  private syncStateData?: ISyncStateData;
  private pos: string | undefined;
  private failCount = 0;
  private notifEvents: XHubChatEvent[] = []; // accumulator of sync events in the current sync response

  // map of extension name to req/resp handler
  private extensions: Record<string, Extension<any, any>> = {};

  /**
   * Create a new sliding sync instance
   * @param lists - The lists to use for sliding sync.
   * @param client - The client to use for /sync calls.
   * @param opts - Stored client options to use for this sliding sync instance.
   * @param syncOpts - Options to control the sync behavior.
   * @param timeoutMS - The number of milliseconds to wait for a response.
   */
  public constructor(
    lists: Map<string, SlidingSyncList>,
    private readonly client: XHubChatClient,
    private opts: Omit<IStartClientOpts, 'slidingSync'> | undefined,
    private syncOpts: SyncApiOptions,
    private readonly timeoutMS?: number,
  ) {
    super();

    const extensions: Extension<any, any>[] = [
      new ExtensionPresence(this.client),
      new ExtensionReceipts(this.client),

    ];
    extensions.forEach((ext) => {
      this.registerExtension(ext);
    });

    this.lists = new Map<string, SlidingList>();
    lists.forEach((list, key) => {
      this.lists.set(key, new SlidingList(list));
    });

    if (client.getNotifTimelineSet()) {
      client.reEmitter.reEmit(
        client.getNotifTimelineSet()!,
        [RoomEvent.Timeline, RoomEvent.TimelineReset],
      );
    }

    // Initialize network monitoring
    this.initializeNetworkMonitor();
  }

  /**
   * Initialize network monitoring for online/offline detection
   */
  private initializeNetworkMonitor(): void {
    if (typeof window !== 'undefined') {
      this.isOnline = window.navigator.onLine;

      window.addEventListener('online', this.handleOnline);
      window.addEventListener('offline', this.handleOffline);
    }
  }

  /**
   * Handle network coming back online
   */
  private handleOnline = (): void => {
    this.isOnline = true;
    this.syncOpts.logger.info('Network back online');

    if (this.connectionState !== ConnState.Connected && !this.isManualStop) {
      this.syncOpts.logger.info('Attempting reconnection after network restored');
      this.reconnect();
    }
  };

  /**
   * Handle network going offline
   */
  private handleOffline = (): void => {
    this.isOnline = false;
    this.connectionState = ConnState.Disconnected;
    this.syncOpts.logger.warn('Network offline detected');
    this.updateSyncState(SyncState.Reconnecting, {
      error: new XHubChatError(new Error('Network offline')),
    });
  };

  /**
   * Register an extension to send with the /sync request.
   * @param ext - The extension to register.
   */
  private registerExtension(ext: Extension<any, any>): void {
    if (this.extensions[ext.name()]) {
      throw new Error(`registerExtension: ${ext.name()} already exists as an extension`);
    }
    this.extensions[ext.name()] = ext;
  }

  /**
   * Set new ranges for an existing list. Calling this function when _only_ the ranges have changed
   * is more efficient than calling setList(index,list) as this function won't resend sticky params,
   * whereas setList always will.
   * @param key - The list key to modify
   * @param ranges - The new ranges to apply.
   *
   * @deprecated This method is for backward compatibility with long-polling architecture.
   * With WebSocket, list changes should trigger server-side events automatically.
   */
  public setListRanges(key: string, ranges: number[][]): void {
    const list = this.lists.get(key);
    if (!list) {
      throw new Error(`no list with key ${key}`);
    }
    list.updateListRange(ranges);
    // Note: With WebSocket architecture, no need to resend/abort requests
    this.syncOpts.logger.debug(`List ranges updated for ${key}`, ranges);
  }

  /**
   * Add or replace a list. Calling this function will interrupt the /sync request to resend new
   * lists.
   * @param key - The key to modify
   * @param list - The new list parameters.
   *
   * @deprecated This method is for backward compatibility with long-polling architecture.
   * With WebSocket, list changes should trigger server-side events automatically.
   */
  public setList(key: string, list: SlidingSyncList): void {
    const existingList = this.lists.get(key);
    if (existingList) {
      existingList.replaceList(list);
      this.lists.set(key, existingList);
    } else {
      this.lists.set(key, new SlidingList(list));
    }
    // Note: With WebSocket architecture, no need to resend/abort requests
    this.syncOpts.logger.debug(`List updated for ${key}`);
  }

  public async onPreExtensionsResponse(ext: Record<string, object>): Promise<void> {
    await Promise.all(
      Object.keys(ext).map(async (extName) => {
        if (this.extensions[extName].when() === ExtensionState.PreProcess) {
          await this.extensions[extName].onResponse(ext[extName]);
        }
      }),
    );
  }

  public async onPostExtensionsResponse(ext: Record<string, object>): Promise<void> {
    await Promise.all(
      Object.keys(ext).map(async (extName) => {
        if (this.extensions[extName].when() === ExtensionState.PostProcess) {
          await this.extensions[extName].onResponse(ext[extName]);
        }
      }),
    );
  }

  /**
   * Returns the current state of this sync object
   * @see XHubChatClient#event:"sync"
   */
  public getSyncState(): SyncState | null {
    return this.syncState;
  }

  /**
   * Purge any events in the notifEvents array. Used after a /sync has been complete.
   * This should not be called at a per-room scope (e.g in onRoomData) because otherwise the ordering
   * will be messed up e.g room A gets a bing, room B gets a newer bing, but both in the same /sync
   * response. If we purge at a per-room scope then we could process room B before room A leading to
   * room B appearing earlier in the notifications timeline, even though it has the higher origin_server_ts.
   */
  private purgeNotifications(): void {
    this.notifEvents.sort((a, b) => {
      return a.getTs() - b.getTs();
    });
    this.notifEvents.forEach((event) => {
      this.client.getNotifTimelineSet()?.addLiveEvent(event, { addToState: false });
    });
    this.notifEvents = [];
  }

  /**
   * Sets the sync state and emits an event to say so
   * @param newState - The new state string
   * @param data - Object of additional data to emit in the event
   */
  private updateSyncState(newState: SyncState, data?: ISyncStateData): void {
    const old = this.syncState;
    this.syncState = newState;
    this.syncStateData = data;
    this.client.emit(ClientEvent.Sync, this.syncState ?? {}, old, data);
  }

  private shouldAbortSync(error: XHubChatError): boolean {
    if (error.errcode === 'M_UNKNOWN_TOKEN') {
      // The logout already happened, we just need to stop.
      this.syncOpts.logger.warn('Token no longer valid - assuming logout');
      this.stop();
      this.updateSyncState(SyncState.Error, { error });
      return true;
    }
    return false;
  }

  /**
   * The last known `pos` from the server.
   * @param state
   * @param resp
   * @param err
   */
  public onLifecycle(state: SlidingSyncState, resp: SlidingSyncResponse | null, err?: Error): void {
    if (err) this.syncOpts.logger.debug('onLifecycle', state, err);

    switch (state) {
      case SlidingSyncState.Complete:
        this.purgeNotifications();
        if (!resp) break;

        // Element won't stop showing the initial loading spinner unless we fire SyncState.Prepared
        if (!this.lastPos) {
          this.updateSyncState(SyncState.Prepared, {
            oldSyncToken: undefined,
            nextSyncToken: resp.pos,
            catchingUp: false,
            fromCache: false,
          });
        }
        // Conversely, Element won't show the room list unless there is at least 1x SyncState.Syncing
        // so hence for the very first sync we will fire prepared then immediately syncing.
        this.updateSyncState(SyncState.Syncing, {
          oldSyncToken: this.lastPos!,
          nextSyncToken: resp.pos,
          catchingUp: false,
          fromCache: false,
        });

        this.lastPos = resp.pos;

        this.client.store.save(true);

        break;
      case SlidingSyncState.RequestFinished:
        if (err) {
          this.failCount += 1;
          this.updateSyncState(
            this.failCount > FAILED_SYNC_ERROR_THRESHOLD ? SyncState.Error : SyncState.Reconnecting,
            {
              error: new XHubChatError(err),
            },
          );
          if (this.shouldAbortSync(new XHubChatError(err))) {
            // shouldAbortSync actually stops syncing too so we don't need to do anything.
          }
        } else {
          this.failCount = 0;
          this.syncOpts.logger.debug(
            `SlidingSyncState.RequestFinished with ${Object.keys(resp?.lists || []).length} rooms`,
          );
        }
        break;
    }
  }

  public async onRoomData(roomId: string, roomData: RoomData): Promise<void> {
    let room = this.client.getRoom(roomId);
    if (!room) {
      if (!roomData.initial) {
        this.syncOpts.logger.debug('initial flag not set but no stored room exists for room ', roomId, roomData);
        return;
      }
      room = _createAndReEmitRoom(this.client, roomId, roomData, this.opts ?? {});
    }
    await this.processRoomData(this.client, room!, roomData);
  }

  private async processRoomData(client: XHubChatClient, room: Room, roomData: RoomData): Promise<void> {
    const stateEvents = mapEvents(this.client, room.roomId, roomData.required_state);
    // Prevent events from being decrypted ahead of time
    // this helps large account to speed up faster
    // room::decryptCriticalEvent is in charge of decrypting all the events
    // required for a client to function properly
    let timelineEvents = mapEvents(this.client, room.roomId, roomData.timeline);

    if (roomData.limited || roomData.initial) {
      // we should not know about any of these timeline entries if this is a genuinely new room.
      // If we do, then we've effectively done scrollback (e.g requesting timeline_limit: 1 for
      // this room, then timeline_limit: 50).
      const knownEvents = new Set<string>();
      room.getLiveTimeline().getEvents().forEach(e => knownEvents.add(e.getId()!));
      // all unknown events BEFORE a known event must be scrollback e.g:
      //       D E   <-- what we know
      // A B C D E F <-- what we just received
      // means:
      // A B C       <-- scrollback
      //       D E   <-- dupes
      //           F <-- new event
      // We bucket events based on if we have seen a known event yet.
      const oldEvents: XHubChatEvent[] = [];
      const newEvents: XHubChatEvent[] = [];
      let seenKnownEvent = false;
      for (let i = timelineEvents.length - 1; i >= 0; i--) {
        const recvEvent = timelineEvents[i];
        if (knownEvents.has(recvEvent.getId()!)) {
          seenKnownEvent = true;
          continue; // don't include this event, it's a dupe
        }
        if (seenKnownEvent) {
          // old -> new
          oldEvents.push(recvEvent);
        } else {
          // old -> new
          newEvents.unshift(recvEvent);
        }
      }
      timelineEvents = newEvents;
      if (oldEvents.length > 0) {
        // old events are scrollback, insert them now
        room.addEventsToTimeline(oldEvents, true, false, room.getLiveTimeline(), roomData.prev_batch);
      }
    }

    // Update unread counts
    // if (roomData.message_new_count) {
    room.setUnreadNotificationCount(NotificationCountType.Total, roomData.message_new_count);
    // }

    if (roomData.bump_stamp) {
      room.setBumpStamp(roomData.bump_stamp);
    }

    if (roomData.limited) {
      // set the back-pagination token. Do this *before* adding any
      // events so that clients can start back-paginating.
      room.getLiveTimeline().setPaginationToken(roomData.prev_batch ?? null, EventTimeline.BACKWARDS);
    }

    await this.injectRoomEvents(room, stateEvents, timelineEvents, roomData.num_live);

    // local fields must be set before any async calls because call site assumes
    // synchronous execution prior to emitting SlidingSyncState.Complete
    room.updateMyMembership(KnownMembership.Join);

    const _roomData = { ...roomData };
    const event = timelineEvents[0];
    if (roomData.timeline.length === 1 && event) {
      if (event.isRedaction() && event.event.id?.includes('post')) { // Check if unlike post
        _roomData.liked = false;
        _roomData.likes = Math.max(0, (room.getRoomData().likes || 0) - 1);
      } else if (event.isReaction() && event.event.id?.includes('post')) { // Check if like post
        _roomData.liked = true;
        _roomData.likes = (room.getRoomData().likes || 0) + 1;
      }
    }

    // Check if room is post and have new comments then increment total_comments
    if (room.getCategory() === RoomCategory.Post
      && roomData.timeline.length >= 1
      && event?.isMessage()
      && !roomData.initial) { // When initial the timeline always have at least 1 event (the post content), then skip
      _roomData.total_comments = (room.getRoomData().total_comments || 0) + 1;
    }

    const newRoomData = room.updateRoomData(_roomData);
    room.recalculate();

    // Store to memory store
    client.store.storeRoom(room);
    // Store room data separately for quick access
    client.store.setRoomsData({ [newRoomData.id]: ({
      ...newRoomData,
      timeline: [], // do not store timeline in cache
      last_message: room.getCategory() === RoomCategory.ChatRoom
        ? {
            ...roomData.last_message,
            content: roomData.last_message?.content?.text,
          }
        : null,
    } as RoomData) });

    if (roomData.initial) {
      client.emit(ClientEvent.Room, room);
    }

    // check if any timeline events should bing and add them to the notifEvents array:
    // we'll purge this once we've fully processed the sync response
    this.addNotifications(timelineEvents);

    const processRoomEvent = async (e: XHubChatEvent): Promise<void> => {
      client.emit(ClientEvent.Event, e);
    };

    await promiseMapSeries(stateEvents, processRoomEvent);
    await promiseMapSeries(timelineEvents, processRoomEvent);
  }

  /**
   * Injects events into a room's model.
   * @param room - The room to inject events into.
   * @param stateEventList - A list of state events. This is the state
   * at the *END* of the timeline list if it is supplied.
   * @param timelineEventList - A list of timeline events. Lower index
   * is earlier in time. Higher index is later.
   * @param numLive - the number of events in timelineEventList which just happened,
   * supplied from the server.
   */
  public async injectRoomEvents(room: Room, stateEventList: XHubChatEvent[], timelineEventList: XHubChatEvent[] = [], numLive: number = 0): Promise<void> {
    // If there are no events in the timeline yet, initialize it with
    // the given state events

    const liveTimeline = room.getLiveTimeline();
    const timelineWasEmpty = liveTimeline.getEvents().length === 0;

    // Check if timeline was empty before adding events
    if (timelineWasEmpty) {
      // Then we can initialize the state of the timeline
      liveTimeline.initializeState(stateEventList);
    }

    if (!timelineWasEmpty) {
      room.getLiveTimeline().getState(EventTimeline.BACKWARDS)?.setStateEvents(stateEventList);
      room.getLiveTimeline().getState(EventTimeline.FORWARDS)?.setStateEvents(stateEventList);
    }

    let liveTimelineEvents: XHubChatEvent[] = [];
    if (numLive > 0) {
      // last numLive events are live
      liveTimelineEvents = timelineEventList.slice(-1 * numLive);
      // everything else is not live
      timelineEventList = timelineEventList.slice(0, -1 * liveTimelineEvents.length);
    }

    // timelineEventList - first load from server or cache
    await room.addLiveEvents(timelineEventList, {
      fromCache: true,
      addToState: false,
    });

    // liveTimelineEvents - there are live events when connect and receive from realtime (Centrifuge)
    if (liveTimelineEvents.length > 0) {
      await room.addLiveEvents(liveTimelineEvents, {
        fromCache: false,
        addToState: false,
      });
    }
  }

  /**
   * Takes a list of timelineEvents and adds and adds to notifEvents
   * as appropriate.
   * This must be called after the room the events belong to has been stored.
   *
   * @param timelineEventList - A list of timeline events. Lower index
   * is earlier in time. Higher index is later.
   */
  private addNotifications(timelineEventList: XHubChatEvent[]): void {
    // gather our notifications into this.notifEvents
    if (!this.client.getNotifTimelineSet()) return;
    for (const timelineEvent of timelineEventList) {
      this.notifEvents.push(timelineEvent);
    }
  }

  /**
   * Reconnect to WebSocket with exponential backoff strategy
   */
  private reconnect(): void {
    if (this.isManualStop) {
      this.syncOpts.logger.debug('Reconnect aborted: manual stop');
      return;
    }

    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      this.syncOpts.logger.error('Max reconnection attempts reached');
      this.updateSyncState(SyncState.Error, {
        error: new XHubChatError(new Error('Max reconnection attempts reached')),
      });
      return;
    }

    this.connectionState = ConnState.Reconnecting;
    this.reconnectAttempts++;

    const strategy = this.getReconnectionStrategy();

    this.syncOpts.logger.info(
      `Reconnecting (attempt ${strategy.attempt}/${strategy.maxAttempts}) in ${strategy.delay}ms`,
    );

    this.updateSyncState(SyncState.Reconnecting, {
      error: new XHubChatError(new Error(`Reconnecting (attempt ${strategy.attempt})`)),
    });

    // Clear any existing timer
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }

    this.reconnectTimer = setTimeout(async () => {
      try {
        await this.initializeWebSocket();
      } catch (error) {
        this.syncOpts.logger.error('Reconnection failed', error);
        this.reconnect(); // Try again
      }
    }, strategy.delay);
  }

  /**
   * Get reconnection strategy with exponential backoff
   */
  private getReconnectionStrategy(): ReconnectionStrategy {
    // Exponential backoff: 1s, 2s, 4s, 8s, 16s, 30s (max)
    const delay = Math.min(
      this.reconnectDelay * (this.reconnectBackoffMultiplier ** (this.reconnectAttempts - 1)),
      this.maxReconnectDelay,
    );

    return {
      attempt: this.reconnectAttempts,
      delay,
      maxAttempts: this.maxReconnectAttempts,
      shouldRetry: this.reconnectAttempts < this.maxReconnectAttempts,
    };
  }

  /**
   * Initialize WebSocket connection
   */
  private async initializeWebSocket(): Promise<void> {
    const realtime = this.client.getRealtime();

    if (!realtime) {
      throw new Error('Realtime connection not available');
    }

    // Setup WebSocket event listeners
    this.setupRealtimeListeners(realtime);

    // Connect to WebSocket
    await realtime.connect();

    this.connectionState = ConnState.Connected;
    this.reconnectAttempts = 0;
    this.reconnectDelay = 1000; // Reset delay

    // Process queued messages
    await this.processMessageQueue();
  }

  /**
   * Setup realtime event listeners
   */
  private setupRealtimeListeners(realtime: CentrifugeRealtime): void {
    // Connection events
    realtime.on(RealtimeEvent.Connected, this.handleConnected);
    realtime.on(RealtimeEvent.Disconnected, this.handleDisconnected);
    realtime.on(RealtimeEvent.Error, this.handleRealtimeError);

    // Data events
    realtime.on(RealtimeEvent.NewMessage, this.handleNewMessage);
    realtime.on(RealtimeEvent.Presence, this.handlePresence);
    realtime.on(RealtimeEvent.RoomUpdated, this.handleRoomUpdated);
  }

  /**
   * Handle WebSocket connected event
   */
  private handleConnected = (): void => {
    this.syncOpts.logger.debug('WebSocket connected');
    this.connectionState = ConnState.Connected;
    this.reconnectAttempts = 0;
    this.lastHeartbeat = Date.now();

    // Process any queued messages
    this.processMessageQueue().catch((error: Error) => {
      this.syncOpts.logger.error('Failed to process message queue', error);
    });
  };

  /**
   * Handle WebSocket disconnected event
   */
  private handleDisconnected = (): void => {
    this.syncOpts.logger.warn('WebSocket disconnected');
    this.connectionState = ConnState.Disconnected;

    if (!this.isManualStop && this.isOnline) {
      this.reconnect();
    } else {
      this.updateSyncState(SyncState.Stopped);
    }
  };

  /**
   * Handle WebSocket error event
   */
  private handleRealtimeError = (error: { code?: number; message: string; cause?: unknown }): void => {
    this.syncOpts.logger.error('WebSocket error', error);
    this.connectionState = ConnState.Error;

    if (!this.isManualStop) {
      this.reconnect();
    }
  };

  /**
   * Handle new message from WebSocket
   */
  private handleNewMessage = async (payload: RawNotiMessage): Promise<void> => {
    try {
      this.syncOpts.logger.debug('New message received', payload);
      // Convert payload to room data
      const roomId = this.extractRoomIdFromPayload(payload);
      const roomData = await this.convertMessageToRoomData(payload);

      if (roomData && roomId) {
        await this.onRoomData(roomId, roomData);

        // Update cache
        await this.updateRoomInCache();
      }

      this.lastHeartbeat = Date.now();
    } catch (error) {
      this.syncOpts.logger.error('Failed to handle new message', error);
    }
  };

  /**
   * Handle presence update from WebSocket
   */
  private handlePresence = async (presence: Record<string, boolean>): Promise<void> => {
    try {
      const resp: SlidingSyncResponse = {
        extensions: { presence },
        lists: {},
        pos: this.lastPos || new Date().getTime().toString(),
        rooms: {},
      };

      await this.onPreExtensionsResponse(resp.extensions);
      this.onLifecycle(SlidingSyncState.Complete, resp);
      await this.onPostExtensionsResponse(resp.extensions);

      this.lastHeartbeat = Date.now();
    } catch (error) {
      this.syncOpts.logger.error('Failed to handle presence', error);
    }
  };

  /**
   * Handle room updated event from WebSocket
   */
  private handleRoomUpdated = async (roomUpdate: RealtimeRoomUpdatePayload): Promise<void> => {
    const detail = roomUpdate.detail;
    const roomData = await this.client.convertServerResToSdkRes({
      has_next: false,
      items: [{
        ...detail,
        category: roomUpdate.transaction_type === RealtimeTransactionType.Chat
          ? RoomCategory.ChatRoom
          : RoomCategory.Post,
      }],
      next_cursor: null,
      total_count: 1,
    });

    try {
      if (detail && detail.id) {
        this.onLifecycle(SlidingSyncState.RequestFinished, roomData, undefined);

        if (!roomData) return;
        await this.onRoomData(detail.id, roomData.rooms[detail.id]);

        // Update cache
        await this.updateRoomInCache();

        this?.onLifecycle(SlidingSyncState.Complete, roomData);
      }

      this.lastHeartbeat = Date.now();
    } catch (error) {
      this.syncOpts.logger.error('Failed to handle room update', error);
    }
  };

  /**
   * Start health check interval
   */
  private startHealthCheck(): void {
    this.stopHealthCheck();

    this.healthCheckInterval = setInterval(() => {
      this.checkConnectionHealth();
    }, this.healthCheckIntervalMs);
  }

  /**
   * Stop health check interval
   */
  private stopHealthCheck(): void {
    if (this.healthCheckInterval) {
      clearInterval(this.healthCheckInterval);
      this.healthCheckInterval = undefined;
    }
  }

  /**
   * Check connection health and force reconnect if needed
   */
  private checkConnectionHealth(): void {
    if (this.connectionState !== ConnState.Connected) return;

    const now = Date.now();
    const timeSinceLastHeartbeat = now - (this.lastHeartbeat || now);

    if (timeSinceLastHeartbeat > this.heartbeatTimeout) {
      this.syncOpts.logger.warn(
        `No heartbeat for ${timeSinceLastHeartbeat}ms, forcing reconnection...`,
      );

      // Force reconnection
      this.client.getRealtime()?.disconnect();
      this.reconnect();
    }
  }

  /**
   * Queue a message for later delivery when connection is restored
   */
  private queueMessage(roomId: string, data: any): void {
    if (this.messageQueue.length >= this.maxQueueSize) {
      // Remove oldest message
      this.messageQueue.shift();
    }

    this.messageQueue.push({
      id: `${roomId}_${Date.now()}_${Math.random()}`,
      roomId,
      data,
      timestamp: Date.now(),
      retries: 0,
    });

    this.syncOpts.logger.debug(`Message queued (${this.messageQueue.length} in queue)`);
  }

  /**
   * Process queued messages after connection is restored
   */
  private async processMessageQueue(): Promise<void> {
    if (this.messageQueue.length === 0) return;

    this.syncOpts.logger.info(`Processing ${this.messageQueue.length} queued messages`);

    const messagesToProcess = [...this.messageQueue];
    this.messageQueue = [];

    for (const msg of messagesToProcess) {
      try {
        await this.sendMessage(msg.roomId, msg.data);
      } catch (error) {
        this.syncOpts.logger.error('Failed to process queued message', error);

        // Re-queue if retries available
        if (msg.retries < 3) {
          msg.retries++;
          this.messageQueue.push(msg);
        }
      }
    }
  }

  /**
   * Send a message via WebSocket or queue if offline
   */
  private async sendMessage(roomId: string, data: any): Promise<void> {
    if (this.connectionState !== ConnState.Connected) {
      this.queueMessage(roomId, data);
      return;
    }

    // Implement actual message sending logic
    // Note: CentrifugeRealtime doesn't have send method directly
    // This is a placeholder - adjust based on your realtime implementation
    this.syncOpts.logger.debug(`Would send message to room ${roomId}`, data);
  }

  /**
   * Update room data in IndexedDB cache
   */
  private async updateRoomInCache(): Promise<void> {
    try {
      this.client.store.save(true);
    } catch (error) {
      this.syncOpts.logger.error(`Failed to update room in cache`, error);
    }
  }

  /**
   * Extract room ID from realtime payload
   */
  private extractRoomIdFromPayload(payload: any): string {
    // Extract room ID from payload based on your data structure
    return payload.detail?.room_id || payload.room_id || payload.detail?.post_id || '';
  }

  /**
   * Convert realtime message payload to RoomData format
   * this call when have new message from WebSocket
   * Then use this data to make roomData to update room store
   */
  private async convertMessageToRoomData(payload: RawNotiMessage): Promise<RoomData | null> {
    try {
      const detail = payload.detail as Omit<IEvent, 'content'> & { 'content': string; 'm.relates_to'?: IEventRelation };
      console.log('🚀 ~ SlidingSync ~ convertMessageToRoomData ~ detail:', detail);
      const isCommentTransaction = payload.transaction_type === RealtimeTransactionType.Comment;
      // Return a partial RoomData with only timeline events
      // The full room data structure will be merged with existing room data
      const rs = {
        ...(!isCommentTransaction && { description: detail.content }),
        timeline: [payload.echoReactEvent // Use echo event if available
          ?? ((isCommentTransaction && !!(detail as unknown as Comment).commenter)
            ? (this.client.mapperCommentResponse([payload.detail])[0] ?? {})
            : {
                type: EventType.RoomMessage,
                id: detail.id,
                sender_id: detail.sender_id,
                timestamp: detail.timestamp || Date.now(),
                content: {
                  text: detail.content,
                  msgtype: MsgType.Text,
                  ...(detail['m.relates_to'] && {
                    'm.relates_to': detail['m.relates_to'],
                  }),
                },
                unsigned: {
                  age: Date.now() - (detail.timestamp || Date.now()),
                  transaction_id: payload.key,
                },
                created_at: detail.created_at || new Date().toISOString(),
              }) as IRoomEvent],
        required_state: [
          ...(isCommentTransaction && !!(detail as unknown as Comment).commenter
            ? [{
                type: 'm.room.member',
                state_key: (detail as unknown as Comment).commenter_id,
                content: {
                  membership: KnownMembership.Join,
                  avatar_url: (detail as unknown as Comment).commenter.avatar,
                  displayname: `${(detail as unknown as Comment).commenter.first_name} ${(detail as unknown as Comment).commenter.last_name}`,
                },
                created_at: (detail as unknown as Comment).created_at,
                sender_id: (detail as unknown as Comment).commenter_id,
                timestamp: detail.timestamp || Date.now(),
                id: `${detail.id}_member`,
              }]
            : []) as IStateEvent[],
        ],
        num_live: 1,
        new_message_count: payload.new_message_count,
        ...(!!payload.new_message_count && { message_new_count: payload.new_message_count }),
        initial: false,
        limited: false,
        ...(!payload.echoReactEvent && {
          last_message: { // IF have echoReactEvent then its reaction, don't update last_message
            ...detail,
            content: {
              text: detail.content,
              msgtype: MsgType.Text,
            },
          },
        }),
        bump_stamp: new Date(detail.timestamp || detail.created_at).getTime(),
      } as Partial<RoomData> as RoomData;

      console.log('🚀 ~ SlidingSync ~ convertMessageToRoomData ~ rs:', rs);
      return rs;
    } catch (error) {
      this.syncOpts.logger.error('Failed to convert message to room data', error);
      return null;
    }
  }

  /**
   * Start syncing with the server using WebSocket architecture
   * Flow: Cache → Server HTTP → WebSocket → Health Check
   */
  public async start(): Promise<void> {
    this.syncOpts.logger.info('Starting WebSocket sync...');

    this.isManualStop = false;
    this.reconnectAttempts = 0;

    try {
      // Step 1: Initialize WebSocket connection
      await this.initializeWebSocket();

      // Step 2: Start health check
      this.startHealthCheck();

      // this.updateSyncState(SyncState.Syncing);

      this.syncOpts.logger.debug('WebSocket sync started successfully');
    } catch (error) {
      this.syncOpts.logger.error('Failed to start sync', error);
      this.handleConnectionError(error as Error);
    }
  }

  /**
   * Handle connection error during start
   */
  private handleConnectionError(error: Error): void {
    this.connectionState = ConnState.Error;
    this.updateSyncState(SyncState.Error, {
      error: new XHubChatError(error),
    });

    if (!this.shouldAbortSync(new XHubChatError(error))) {
      // Try to reconnect if it's not a fatal error
      this.reconnect();
    }
  }

  /**
   * Stop syncing with the server
   * Cleanup all resources: WebSocket, timers, event listeners
   */
  public stop(): void {
    this.syncOpts.logger.debug('Stopping WebSocket sync...');

    this.isManualStop = true;

    // Clear reconnection timer
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = undefined;
    }

    // Reset reconnection state
    this.reconnectAttempts = 0;
    this.reconnectDelay = 1000;

    // Stop health check
    this.stopHealthCheck();

    // Disconnect WebSocket
    const realtime = this.client.getRealtime();
    if (realtime) {
      realtime.disconnect();

      // Remove WebSocket event listeners
      realtime.off(RealtimeEvent.Connected, this.handleConnected);
      realtime.off(RealtimeEvent.Disconnected, this.handleDisconnected);
      realtime.off(RealtimeEvent.Error, this.handleRealtimeError);
      realtime.off(RealtimeEvent.NewMessage, this.handleNewMessage);
      realtime.off(RealtimeEvent.Presence, this.handlePresence);
      realtime.off(RealtimeEvent.RoomUpdated, this.handleRoomUpdated);
    }

    // Cleanup network monitor
    if (typeof window !== 'undefined') {
      window.removeEventListener('online', this.handleOnline);
      window.removeEventListener('offline', this.handleOffline);
    }

    // Remove all event listeners
    this.removeAllListeners(SlidingSyncEvent.Lifecycle);
    this.removeAllListeners(SlidingSyncEvent.RoomData);

    this.connectionState = ConnState.Disconnected;
    this.updateSyncState(SyncState.Stopped);

    this.syncOpts.logger.debug('WebSocket sync stopped');
  }
}
